import { gql, useQuery } from '@apollo/client'
import { Trans } from '@lingui/macro'
import { Trace, TraceEvent } from '@uniswap/analytics'
import { BrowserEvent, InterfaceElementName, InterfaceEventName, InterfacePageName } from '@uniswap/analytics-events'
import { useWeb3React } from '@web3-react/core'
import { useToggleAccountDrawer } from 'components/AccountDrawer'
import { ButtonGray, ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import { FlyoutAlignment, Menu } from 'components/Menu'
import PositionList from 'components/PositionList'
import {
  PositionMetrics,
  PositionSortDirection,
  PositionSortKey,
  SortablePositionHeader,
} from 'components/PositionList/PositionTable'
import { RowBetween, RowFixed } from 'components/Row'
import { SwitchLocaleLink } from 'components/SwitchLocaleLink'
import { isSupportedChain } from 'constants/chains'
import { analyticsApolloClient } from 'graphql/thegraph/apollo'
import { useFilterPossiblyMaliciousPositions } from 'hooks/useFilterPossiblyMaliciousPositions'
import { useV3Positions } from 'hooks/useV3Positions'
import { useCallback, useMemo, useState } from 'react'
import { AlertTriangle, BookOpen, ChevronDown, ChevronsRight, Inbox, Layers } from 'react-feather'
import { Link } from 'react-router-dom'
import styled, { css, useTheme } from 'styled-components/macro'
import { HideSmall, ThemedText } from 'theme'
import { PositionDetails } from 'types/position'

import { V2_FACTORY_ADDRESSES } from '../../constants/addresses'
import CTACards from './CTACards'
import { LoadingRows } from './styleds'
import V2Positions, { V2LiquiditySummary } from './V2Positions'

const PageWrapper = styled(AutoColumn)`
  padding: 48px 16px 0;
  max-width: 1120px;
  width: 100%;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    max-width: 800px;
  `};

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    max-width: 500px;
  `};

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 48px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding-top: 20px;
  }
`
const Hero = styled.div`
  display: grid;
  gap: 8px;
  padding: 8px 0 18px;
`

const HeroTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 32px;
  line-height: 1.15;
`

const HeroSubtitle = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 16px;
`
const TitleRow = styled(RowBetween)`
  color: ${({ theme }) => theme.textSecondary};
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex-wrap: wrap;
    gap: 12px;
    width: 100%;
  `};
`
const ButtonRow = styled(RowFixed)`
  & > *:not(:last-child) {
    margin-left: 8px;
  }

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    flex-direction: row-reverse;
  `};
`
const PoolMenu = styled(Menu)`
  margin-left: 0;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex: 1 1 auto;
    width: 49%;
    right: 0px;
  `};

  a {
    width: 100%;
  }
`
const PoolMenuItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-weight: 500;
`
const MoreOptionsButton = styled(ButtonGray)`
  border-radius: 12px;
  flex: 1 1 auto;
  padding: 6px 8px;
  width: 100%;
  background-color: ${({ theme }) => theme.backgroundSurface};
  margin-right: 8px;
`

const MoreOptionsText = styled(ThemedText.DeprecatedBody)`
  align-items: center;
  display: flex;
`

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  max-width: 300px;
  min-height: 25vh;
`

const IconStyle = css`
  width: 48px;
  height: 48px;
  margin-bottom: 0.5rem;
`

const NetworkIcon = styled(AlertTriangle)`
  ${IconStyle}
`

const InboxIcon = styled(Inbox)`
  ${IconStyle}
`

const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  border-radius: 12px;
  font-size: 16px;
  padding: 6px 8px;
  width: fit-content;
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    flex: 1 1 auto;
    width: 100%;
  `};
`

const MainContentWrapper = styled.main`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 0;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    grid-template-columns: 1fr;
  `};
`

const SummaryCard = styled.div`
  min-width: 0;
  padding: 15px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`

const SummaryLabel = styled(ThemedText.Caption)`
  font-size: 11px;
`

const SummaryValue = styled.div`
  margin-top: 8px;
  overflow: hidden;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 22.4px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SummaryDetail = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 9.6px;
`

const PositionsHeading = styled.h2`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 22px;
`

const PositionsHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const ProtocolFilters = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
`

const PositionFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`

const FilterGroup = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
`

const ProtocolFilter = styled.button<{ $active: boolean }>`
  min-width: 58px;
  height: 36px;
  padding: 0 12px;
  color: ${({ $active, theme }) => ($active ? theme.accentAction : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.accentActionSoft : 'transparent')};
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
  }
`

const LiquidityTools = styled.div`
  display: grid;
  grid-template-columns: auto minmax(220px, 1fr);
  gap: 12px;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    grid-template-columns: 1fr;
  `};
`

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.accentAction};
  }
`

const POSITION_CREATED_QUERY = gql`
  query PositionCreatedTimestamps($ids: [ID!]!) {
    positions(where: { id_in: $ids }) {
      id
      transaction {
        timestamp
      }
    }
  }
`

function PositionsLoadingPlaceholder() {
  return (
    <LoadingRows>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </LoadingRows>
  )
}

function WrongNetworkCard() {
  const theme = useTheme()

  return (
    <>
      <PageWrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <TitleRow padding="0">
              <ThemedText.LargeHeader>
                <Trans>Pools</Trans>
              </ThemedText.LargeHeader>
            </TitleRow>

            <MainContentWrapper>
              <ErrorContainer>
                <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                  <NetworkIcon strokeWidth={1.2} />
                  <div data-testid="pools-unsupported-err">
                    <Trans>Your connected network is unsupported.</Trans>
                  </div>
                </ThemedText.DeprecatedBody>
              </ErrorContainer>
            </MainContentWrapper>
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
      <SwitchLocaleLink />
    </>
  )
}

export function sortPositionsByEstimatedValue(
  positions: PositionDetails[],
  positionMetrics: Record<string, { estimatedValue?: number }>,
  sortDirection: 'asc' | 'desc'
) {
  return [...positions].sort((a, b) => {
    const aValue = positionMetrics[a.tokenId.toString()]?.estimatedValue
    const bValue = positionMetrics[b.tokenId.toString()]?.estimatedValue
    if (aValue === undefined && bValue === undefined) return 0
    if (aValue === undefined) return 1
    if (bValue === undefined) return -1
    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
  })
}

export function sortPositionMetricKeys(
  positionKeys: string[],
  positionMetrics: Record<string, PositionMetrics>,
  sortKey: PositionSortKey,
  sortDirection: PositionSortDirection
) {
  const metricName: keyof PositionMetrics =
    sortKey === 'value' ? 'estimatedValue' : sortKey === 'fees' ? 'feesUSD' : 'createdAt'
  return [...positionKeys].sort((firstKey, secondKey) => {
    const firstValue = positionMetrics[firstKey]?.[metricName]
    const secondValue = positionMetrics[secondKey]?.[metricName]
    if (firstValue === undefined && secondValue === undefined) return firstKey.localeCompare(secondKey)
    if (firstValue === undefined) return 1
    if (secondValue === undefined) return -1
    const difference = firstValue - secondValue
    return sortDirection === 'asc' ? difference : -difference
  })
}

export default function Pool() {
  const { account, chainId } = useWeb3React()
  const toggleWalletDrawer = useToggleAccountDrawer()

  const theme = useTheme()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortKey, setSortKey] = useState<PositionSortKey>('value')
  const [sortDirection, setSortDirection] = useState<PositionSortDirection>('desc')
  const [protocolFilter, setProtocolFilter] = useState<'all' | 'v2' | 'v3'>('all')
  const [rangeFilter, setRangeFilter] = useState<'all' | 'in-range' | 'out-of-range'>('all')
  const [v2Summary, setV2Summary] = useState<V2LiquiditySummary>({ oneLocked: 0, positions: 0 })
  const [positionMetrics, setPositionMetrics] = useState<Record<string, PositionMetrics>>({})

  const { positions, loading: positionsLoading } = useV3Positions(account)

  const filteredPositions = useFilterPossiblyMaliciousPositions(positions ?? [])
  const positionIds = useMemo(() => filteredPositions.map(({ tokenId }) => tokenId.toString()), [filteredPositions])
  const { data: positionCreatedData } = useQuery<{
    positions: Array<{ id: string; transaction?: { timestamp?: string } }>
  }>(POSITION_CREATED_QUERY, {
    client: analyticsApolloClient,
    variables: { ids: positionIds },
    skip: positionIds.length === 0,
  })
  const createdAtByTokenId = useMemo(
    () =>
      Object.fromEntries(
        (positionCreatedData?.positions ?? []).flatMap((position) => {
          const timestamp = Number(position.transaction?.timestamp)
          return Number.isFinite(timestamp) ? [[position.id, timestamp]] : []
        })
      ),
    [positionCreatedData]
  )
  const oneLocked = useMemo(
    () =>
      Object.entries(positionMetrics)
        .filter(([positionKey]) => positionKey.startsWith('v3:'))
        .reduce<number>((total, [, metrics]) => total + (metrics.oneLocked ?? 0), 0),
    [positionMetrics]
  )
  const valueInUSD = useMemo(() => {
    const pricedPositions = Object.entries(positionMetrics)
      .filter(([positionKey]) => positionKey.startsWith('v3:'))
      .map(([, metrics]) => metrics.estimatedValue)
      .filter((value): value is number => value !== undefined)
    return pricedPositions.length > 0 ? pricedPositions.reduce((total, value) => total + value, 0) : undefined
  }, [positionMetrics])
  const totalV3Fees = useMemo(() => {
    const pricedFees = Object.entries(positionMetrics)
      .filter(([positionKey]) => positionKey.startsWith('v3:'))
      .map(([, metrics]) => metrics.feesUSD)
      .filter((value): value is number => value !== undefined)
    return pricedFees.length > 0 ? pricedFees.reduce((total, value) => total + value, 0) : undefined
  }, [positionMetrics])
  const formattedValueInUSD = valueInUSD?.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const updatePositionMetrics = useCallback((positionKey: string, metrics?: PositionMetrics) => {
    setPositionMetrics((current) => {
      if (!metrics) {
        if (!(positionKey in current)) return current
        const next = { ...current }
        delete next[positionKey]
        return next
      }
      const previous = current[positionKey]
      return previous?.estimatedValue === metrics.estimatedValue &&
        previous?.feesUSD === metrics.feesUSD &&
        previous?.createdAt === metrics.createdAt &&
        previous?.oneLocked === metrics.oneLocked
        ? current
        : { ...current, [positionKey]: metrics }
    })
  }, [])

  const rowOrder = useMemo(() => {
    const knownKeys = new Set([
      ...filteredPositions.map(({ tokenId }) => `v3:${tokenId.toString()}`),
      ...Object.keys(positionMetrics),
    ])
    return Object.fromEntries(
      sortPositionMetricKeys([...knownKeys], positionMetrics, sortKey, sortDirection).map((positionKey, index) => [
        positionKey,
        index + 1,
      ])
    )
  }, [filteredPositions, positionMetrics, sortDirection, sortKey])

  const handleSort = useCallback(
    (nextSortKey: PositionSortKey) => {
      if (nextSortKey === sortKey) {
        setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))
      } else {
        setSortKey(nextSortKey)
        setSortDirection('desc')
      }
    },
    [sortKey]
  )
  const updateV2Summary = useCallback((next: V2LiquiditySummary) => {
    setV2Summary((current) =>
      current.estimatedValue === next.estimatedValue &&
      current.oneLocked === next.oneLocked &&
      current.positions === next.positions
        ? current
        : next
    )
  }, [])

  if (!isSupportedChain(chainId)) {
    return <WrongNetworkCard />
  }

  const showConnectAWallet = Boolean(!account)
  const showV2Features = Boolean(V2_FACTORY_ADDRESSES[chainId])
  const estimatedValues = [valueInUSD, v2Summary.estimatedValue].filter((value): value is number => value !== undefined)
  const totalEstimatedValue = estimatedValues.length
    ? estimatedValues.reduce((total, value) => total + value, 0)
    : undefined
  const totalOneLocked = oneLocked + v2Summary.oneLocked
  const formatUSD = (value: number | undefined) =>
    value?.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) ?? '-'

  const menuItems = [
    {
      content: (
        <PoolMenuItem>
          <Trans>Migrate</Trans>
          <ChevronsRight size={16} />
        </PoolMenuItem>
      ),
      link: '/migrate/v2',
      external: false,
    },
    {
      content: (
        <PoolMenuItem>
          <Trans>Import V2 pool</Trans>
          <Layers size={16} />
        </PoolMenuItem>
      ),
      link: '/pools/v2/find',
      external: false,
    },
    {
      content: (
        <PoolMenuItem>
          <Trans>Learn</Trans>
          <BookOpen size={16} />
        </PoolMenuItem>
      ),
      link: 'https://support.uniswap.org/hc/en-us/categories/8122334631437-Providing-Liquidity-',
      external: true,
    },
  ]

  return (
    <Trace page={InterfacePageName.POOL_PAGE} shouldLogImpression>
      <PageWrapper>
        <AutoColumn gap="lg" justify="center">
          <AutoColumn gap="lg" style={{ width: '100%' }}>
            <Hero>
              <HeroTitle>Supply liquidity to collect fees</HeroTitle>
              <HeroSubtitle>Manage your CasinoHarmony V2 and V3 positions in one place.</HeroSubtitle>
            </Hero>
            <TitleRow padding="0">
              <ButtonRow>
                {showV2Features && (
                  <PoolMenu
                    menuItems={menuItems}
                    flyoutAlignment={FlyoutAlignment.LEFT}
                    ToggleUI={(props: any) => (
                      <MoreOptionsButton {...props}>
                        <MoreOptionsText>
                          <Trans>More</Trans>
                          <ChevronDown size={15} />
                        </MoreOptionsText>
                      </MoreOptionsButton>
                    )}
                  />
                )}
                <ResponsiveButtonPrimary data-cy="join-pool-button" id="join-pool-button" as={Link} to="/add/ETH">
                  + <Trans>New Position</Trans>
                </ResponsiveButtonPrimary>
              </ButtonRow>
            </TitleRow>

            <SummaryGrid>
              <SummaryCard>
                <SummaryLabel color="textSecondary">Total liquidity</SummaryLabel>
                <SummaryValue>{formatUSD(totalEstimatedValue)}</SummaryValue>
                <SummaryDetail>
                  {totalOneLocked.toLocaleString(undefined, { maximumFractionDigits: 6 })} CRO
                </SummaryDetail>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel color="textSecondary">V2 liquidity</SummaryLabel>
                <SummaryValue>{formatUSD(v2Summary.estimatedValue)}</SummaryValue>
                <SummaryDetail>{v2Summary.positions} positions</SummaryDetail>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel color="textSecondary">V3 liquidity</SummaryLabel>
                <SummaryValue>{formattedValueInUSD ?? '-'}</SummaryValue>
                <SummaryDetail>{filteredPositions.length} positions</SummaryDetail>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel color="textSecondary">Total fees</SummaryLabel>
                <SummaryValue>{formatUSD(totalV3Fees)}</SummaryValue>
                <SummaryDetail>V3 unclaimed fees</SummaryDetail>
              </SummaryCard>
            </SummaryGrid>

            <PositionsHeadingRow>
              <PositionsHeading>Your positions</PositionsHeading>
            </PositionsHeadingRow>
            <LiquidityTools>
              <PositionFilters>
                <ProtocolFilters role="tablist" aria-label="Position protocol">
                  {(['all', 'v2', 'v3'] as const).map((protocol) => (
                    <ProtocolFilter
                      key={protocol}
                      type="button"
                      role="tab"
                      aria-selected={protocolFilter === protocol}
                      $active={protocolFilter === protocol}
                      onClick={() => setProtocolFilter(protocol)}
                    >
                      {protocol === 'all' ? 'All' : protocol.toUpperCase()}
                    </ProtocolFilter>
                  ))}
                </ProtocolFilters>
                <FilterGroup role="tablist" aria-label="Position range">
                  {(['all', 'in-range', 'out-of-range'] as const).map((range) => (
                    <ProtocolFilter
                      key={range}
                      type="button"
                      role="tab"
                      aria-selected={rangeFilter === range}
                      $active={rangeFilter === range}
                      onClick={() => setRangeFilter(range)}
                    >
                      {range === 'all' ? 'All' : range === 'in-range' ? 'In range' : 'Out of range'}
                    </ProtocolFilter>
                  ))}
                </FilterGroup>
              </PositionFilters>
              <SearchInput
                aria-label="Search liquidity positions"
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search positions by token, address, or position ID"
                value={searchQuery}
              />
            </LiquidityTools>

            <MainContentWrapper>
              {!account ? (
                <ErrorContainer>
                  <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                    <InboxIcon strokeWidth={1} style={{ marginTop: '2em' }} />
                    <div>
                      <Trans>Connect a wallet to view your V2 and V3 liquidity positions.</Trans>
                    </div>
                  </ThemedText.DeprecatedBody>
                  {showConnectAWallet && (
                    <TraceEvent
                      events={[BrowserEvent.onClick]}
                      name={InterfaceEventName.CONNECT_WALLET_BUTTON_CLICKED}
                      properties={{ received_swap_quote: false }}
                      element={InterfaceElementName.CONNECT_WALLET_BUTTON}
                    >
                      <ButtonPrimary
                        style={{ marginTop: '2em', marginBottom: '2em', padding: '8px 16px' }}
                        onClick={toggleWalletDrawer}
                      >
                        <Trans>Connect a wallet</Trans>
                      </ButtonPrimary>
                    </TraceEvent>
                  )}
                </ErrorContainer>
              ) : (
                <>
                  <SortablePositionHeader sortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} />
                  {positionsLoading ? (
                    <PositionsLoadingPlaceholder />
                  ) : (
                    <PositionList
                      positions={filteredPositions}
                      searchQuery={searchQuery}
                      visible={protocolFilter !== 'v2'}
                      rangeFilter={rangeFilter}
                      createdAtByTokenId={createdAtByTokenId}
                      rowOrder={rowOrder}
                      onPositionMetricsChange={updatePositionMetrics}
                    />
                  )}
                  {showV2Features && (
                    <V2Positions
                      searchQuery={searchQuery}
                      visible={protocolFilter !== 'v3'}
                      rowOrder={rowOrder}
                      positionMetrics={positionMetrics}
                      onSummaryChange={updateV2Summary}
                      onPositionMetricsChange={updatePositionMetrics}
                    />
                  )}
                  {!positionsLoading && filteredPositions.length + v2Summary.positions === 0 && (
                    <ErrorContainer>
                      <ThemedText.DeprecatedBody color={theme.textTertiary} textAlign="center">
                        <InboxIcon strokeWidth={1} style={{ marginTop: '2em' }} />
                        <div>
                          <Trans>Your V2 and V3 liquidity positions will appear here.</Trans>
                        </div>
                      </ThemedText.DeprecatedBody>
                    </ErrorContainer>
                  )}
                </>
              )}
            </MainContentWrapper>
            <HideSmall>
              <CTACards />
            </HideSmall>
          </AutoColumn>
        </AutoColumn>
      </PageWrapper>
      <SwitchLocaleLink />
    </Trace>
  )
}
